<template>
  <div class="brc" @mouseenter="hovered = true" @mouseleave="hovered = false">
    <!-- Image -->
    <div class="brc__img-wrap" @click="viewRecipe">
      <v-img
        :src="previewSrc"
        cover
        height="100%"
        @error="onPreviewError"
      >
        <template #placeholder>
          <div class="brc__img-placeholder">
            <v-progress-circular indeterminate color="white" size="24" />
          </div>
        </template>
      </v-img>

      <!-- Overlay hover -->
      <div class="brc__overlay" :class="{ 'brc__overlay--visible': hovered }">
        <button class="brc__overlay-btn" @click.stop="viewRecipe">
          <v-icon size="14">mdi-eye</v-icon>
          Voir
        </button>
      </div>

      <!-- Corbeille admin — toujours visible -->
      <button
        v-if="isAdmin"
        class="brc__delete-btn"
        @click.stop="$emit('delete', item)"
        aria-label="Supprimer la recette"
      >
        <v-icon size="15">mdi-delete-outline</v-icon>
      </button>
    </div>

    <!-- Corps -->
    <div class="brc__body">
      <p class="brc__title">{{ item.title || 'Sans titre' }}</p>
      <div class="brc__meta">
        <span v-if="item.totals?.kcal" class="brc__meta-item">
          <v-icon size="12">mdi-fire</v-icon>
          {{ Math.round(item.totals.kcal) }} kcal
        </span>
        <span v-if="item.servings" class="brc__meta-item">
          <v-icon size="12">mdi-silverware-fork-knife</v-icon>
          {{ item.servings }} portions
        </span>
      </div>
      <div v-if="item.category" class="brc__tags">
        <span class="brc__tag">{{ item.category ? $t(item.category) : '' }}</span>
      </div>
    </div>

    <!-- Bouton ajouter -->
    <div class="brc__footer">
      <button
        class="brc__add-btn"
        :class="{ 'brc__add-btn--disabled': disabled }"
        :disabled="disabled"
        @click="addToCatalog"
      >
        <v-icon size="14">mdi-plus</v-icon>
        Ajouter à mes recettes
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";

const props = defineProps({
  item: { type: Object, required: true },
  disabled: { type: Boolean, default: false },
  isAdmin: { type: Boolean, default: false },
});

const emit = defineEmits(["add", "view", "delete"]);

const hovered = ref(false);
const validPreview = ref(true);
const placeholderImage = 'https://www.mimisrecipes.com/wp-content/uploads/2018/12/recipe-placeholder-featured.jpg';

const sourceKey = computed(() => props.item?.url || props.item?.image || props.item?.imageUrl || '');
const previewSrc = computed(() => validPreview.value && sourceKey.value ? sourceKey.value : placeholderImage);

function onPreviewError() { validPreview.value = false; }
watch(sourceKey, (v) => { validPreview.value = !!v; });

function addToCatalog() { emit('add', props.item); }
function viewRecipe() { emit('view', props.item.id); }
</script>

<style scoped>
.brc {
  display: flex;
  flex-direction: column;
  cursor: default;
}

/* Image */
.brc__img-wrap {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  aspect-ratio: 4 / 3;
  background: rgba(var(--v-theme-text), 0.05);
  cursor: pointer;
}

.brc__img-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Overlay */
.brc__overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
  pointer-events: none;
}
.brc__overlay--visible {
  background: rgba(0, 0, 0, 0.28);
  pointer-events: auto;
}

.brc__overlay-btn {
  opacity: 0;
  transform: translateY(6px);
  transition: opacity 0.18s ease, transform 0.18s ease;
  background: #fff;
  border: none;
  border-radius: 8px;
  padding: 7px 14px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  color: #1A1615;
  display: flex;
  align-items: center;
  gap: 5px;
  font-family: inherit;
}
.brc__overlay--visible .brc__overlay-btn {
  opacity: 1;
  transform: translateY(0);
}

/* Corbeille admin */
.brc__delete-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.92);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(180, 40, 40, 0.7);
  transition: background 0.15s, color 0.15s;
  z-index: 3;
}
.brc__delete-btn:hover {
  background: #fff;
  color: rgb(180, 40, 40);
}

/* Corps */
.brc__body {
  padding: 10px 2px 6px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.brc__title {
  margin: 0;
  font-size: 13px;
  font-weight: 500;
  color: rgb(var(--v-theme-text));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
}

.brc__meta {
  display: flex;
  align-items: center;
  gap: 10px;
}

.brc__meta-item {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  color: rgba(var(--v-theme-text), 0.5);
}

.brc__tags {
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
}

.brc__tag {
  font-size: 10px;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(var(--v-theme-text), 0.06);
  color: rgba(var(--v-theme-text), 0.55);
  border: 0.5px solid rgba(var(--v-theme-text), 0.1);
}

/* Footer */
.brc__footer {
  padding: 0 2px 4px;
  margin-top: 2px;
}

.brc__add-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 500;
  color: rgb(var(--v-theme-accent));
  background: rgba(var(--v-theme-accent), 0.07);
  border: 0.5px solid rgba(var(--v-theme-accent), 0.25);
  border-radius: 999px;
  padding: 5px 12px;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s;
  width: 100%;
  justify-content: center;
}
.brc__add-btn:hover:not(.brc__add-btn--disabled) {
  background: rgba(var(--v-theme-accent), 0.14);
}
.brc__add-btn--disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
</style>
